import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import SupportBase, { type SupportBaseScreen } from 'components/Common/SupportBase/SupportBase'
import getCustomerSupportAgentImage from 'components/Common/CustomerSupport/getCustomerSupportAgentImage'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import ContactContext from 'contexts/contactContext'
import GeoContext from 'contexts/geoContext'
import { useResponsive } from 'hooks/useResponsive'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import type { Matches } from 'components/Common/Breakpoint/responsiveHelper'

const CALL_START_ICON = <LinePhoneIcon />

const SupportSlot = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-block: ${rem(20)};
  padding: ${rem(28)} ${rem(24)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${props => props.theme.borderRadius.M};

  ${mediaQueryUp.tablet} {
    padding: ${rem(32)};
  }
`

/* Homepage sits in the default LayoutContainer; do not clamp Lg to 960. */
const PageSupportBase = styled(SupportBase)`
  &.screen-lg {
    max-width: none;
    margin-inline: 0;
  }
`

function getSupportScreen(match: Matches, isMobileDevice: boolean): SupportBaseScreen {
  if (match.desktop || match.largeDesktop || match.extraLargeDesktop) {
    return 'lg'
  }
  if (match.tablet) {
    return 'md'
  }
  if (match.mobile) {
    return 'sm'
  }
  // SSR and the first client paint have no matchMedia yet. Use the UA device
  // already parsed into Redux so desktop does not flash sm → lg.
  return isMobileDevice ? 'sm' : 'lg'
}

interface Props {
  className?: string
}

function Support(props: Props) {
  const { className } = props
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const supportContact = useContext(ContactContext)
  const [match] = useResponsive()
  const isMobileDevice = useIsMobileDevice()
  const screen = getSupportScreen(match, isMobileDevice)
  const is24_7Support = !NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode)
  const phoneNumber = supportContact.local.humanReadable

  const heading = is24_7Support
    ? intl.formatMessage({
        id: '6wQYCPM3sM9_KtaXSlJX',
        defaultMessage: 'Our team is available 24 hours, 7 days',
        description: 'Homepage support heading describing 24/7 customer service availability',
      })
    : intl.formatMessage({
        id: '_jJ6_COFXuOi6qOdDwfd',
        defaultMessage: 'Our team is available 7 days a week from 9am to 7pm',
        description: 'Homepage support heading describing business-hours customer service availability',
      })

  const body = is24_7Support
    ? intl.formatMessage({
        id: 'nuhxPvOAMCRiA_9Y1uwu',
        defaultMessage: 'Call our dedicated customer service team anytime, whenever you need us.',
        description: 'Homepage support body inviting customers to call the service team',
      })
    : intl.formatMessage({
        id: 'p6HBOwcCJzEUjGiUw8h-',
        defaultMessage: 'Call our dedicated customer service team from 9am to 7pm, 7 days a week.',
        description: 'Homepage support body inviting customers in business-hours regions to call during published hours',
      })

  // Single phone CTA only. SupportTile's mobile WhatsApp button is omitted: LE
  // WHATS_APP_COUNTRIES is empty, Global Kit Support is one action, and header/footer still own WhatsApp when that flag is on.
  const buttonLabel = screen === 'sm'
    ? intl.formatMessage({
        id: 'qWBleRgroCoMCtIG5aVK',
        defaultMessage: 'Call now',
        description: 'Homepage support CTA label used on the small-screen full-width button',
      })
    : intl.formatMessage({
        id: 'SswqjzT8r6Dox5eD5QEr',
        defaultMessage: 'Call {phoneNumber}',
        description: 'Homepage support CTA label that includes the local support phone number',
      }, { phoneNumber })

  const supportImageAlt = intl.formatMessage({
    id: 'la00A7GOz4WEJUNy_lzy',
    defaultMessage: 'Customer service specialist',
    description: 'Alt text for the customer support agent photo in the homepage support section',
  })

  const actionButtonProps = useMemo(() => ({
    startIcon: CALL_START_ICON,
    href: `tel:${supportContact.local.number}`,
  }), [supportContact.local.number])

  const drop = useMemo(() => ({
    image: getCustomerSupportAgentImage('right', screen !== 'lg', currentRegionCode),
    alt: supportImageAlt,
  }), [currentRegionCode, screen, supportImageAlt])

  return <SupportSlot className={className}>
    <PageSupportBase
      screen={screen}
      heading={heading}
      body={body}
      buttonLabel={buttonLabel}
      actionButtonProps={actionButtonProps}
      drop={drop}
    />
  </SupportSlot>
}

export default Support
